import React from 'react';
import { Image } from 'react-bootstrap';
import ReactApexChart from 'react-apexcharts';

import { calculateColumnWidth, getInitials } from '../services/function';

import ArrowRightIcon from '../assets/icon/arrowRightCircleB.svg';
import ArrowLeftIcon from '../assets/icon/arrowLeftCircleB.svg';
import statusFeedBackBom from '../assets/icon/statusFeedbackBomPreto.svg';
import statusFeedBackMedio from '../assets/icon/statusFeedbackMedioPreto.svg';
import statusFeedBackRuim from '../assets/icon/statusFeedbackRuimPreto.svg';
import { IResumoAtendimento } from '../interfaces/IResumoAtendimento';

const DashboardUserAtendiment = ({
  atendimentoData,
  setShowDashboardFeedback,
}: {
  atendimentoData: IResumoAtendimento;
  setShowDashboardFeedback: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element => {
  const numBars = atendimentoData.qtdeAtendimentoUsuario?.length || 1;
  const minWidth = 5;
  const maxWidth = 30;

  const dataChartBar = {
    options: {
      chart: {
        type: 'bar' as const,
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          distributed: true,
          columnWidth: calculateColumnWidth(numBars, minWidth, maxWidth),
        },
      },
      toolbar: {
        show: false,
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
      xaxis: {
        labels: {
          style: {
            fontSize: '1rem', // Tamanho da fonte
            fontWeight: 'bold', // Negrito
          },
        },
      },
    },
    series: [
      {
        name: 'Qtde de Atendimentos',
        data:
          atendimentoData.qtdeAtendimentoUsuario?.map((item) => ({
            x: getInitials(item.nomeUsuario),
            y: item.qtdeTotalAtendimento,
          })) || [],
      },
    ],
  };

  return (
    <div className="h-100 d-flex flex-column w-100">
      <div
        className="d-flex align-items-center justify-content-between mt-2 ms-4 me-4"
        style={{ flexShrink: 0 }}
      >
        <span style={{ fontSize: '1.68rem', fontWeight: '600' }}>
          Usuários com mais atendimentos
        </span>
        <div
          className="d-flex"
          onClick={() => setShowDashboardFeedback(true)}
          onKeyDown={() => setShowDashboardFeedback(true)}
          role="button"
          tabIndex={0}
        >
          <Image
            src={ArrowLeftIcon}
            alt="ArrowRightIcon"
            style={{ width: '1.75rem', height: '1.75rem' }}
          />
          <Image
            src={ArrowRightIcon}
            alt="ArrowRightIcon"
            className="ms-2"
            style={{ width: '1.75rem', height: '1.75rem' }}
          />
        </div>
      </div>
      <div className="d-flex" style={{ flexShrink: 0 }}>
        <div
          className="d-flex flex-column justify-content-between ms-2 mt-2 w-100"
          style={{ height: '20rem' }}
        >
          <ReactApexChart
            options={dataChartBar.options}
            series={dataChartBar.series}
            type="bar"
            width="100%"
            height="100%"
          />
        </div>
      </div>
      <div
        className="w-100 overflow-auto"
        style={{
          flex: 1,
          borderRadius: '0.68rem',
          boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
          overflowY: 'auto',
        }}
      >
        <div className="d-flex flex-column" style={{ height: '100px' }}>
          <table
            className="table table-bordered mb-0"
            style={{
              borderRadius: '0.68rem',
              borderCollapse: 'collapse',
              borderColor: '#D9D9D9',
              borderWidth: '2px',
            }}
          >
            <thead
              className=" fw-light text-dark"
              style={{
                backgroundColor: '#D9D9D9',
                borderRadius: '0.68rem!important',
                fontSize: '1.25rem',
                position: 'sticky',
                top: '0',
                zIndex: 1, // Garantir que o cabeçalho fique acima do conteúdo ao rolar
              }}
            >
              <tr
                style={{
                  fontWeight: '600',
                }}
              >
                <th className="col-1">Sigla</th>
                <th className="col-4">Nome</th>
                <th className="col-1 text-center">
                  <Image
                    style={{ width: '2.5rem', height: '2.5rem' }}
                    src={statusFeedBackBom}
                    alt="Status Feedback Positivo"
                  />
                </th>
                <th className="col-1 text-center">
                  <Image
                    style={{ width: '2.5rem', height: '2.5rem' }}
                    src={statusFeedBackMedio}
                    alt="Status Feedback Mediano"
                  />
                </th>
                <th className="col-1 text-center">
                  <Image
                    style={{ width: '2.5rem', height: '2.5rem' }}
                    src={statusFeedBackRuim}
                    alt="Status Feedback Negativo"
                  />
                </th>
                <th
                  className="col-2 text-center"
                  style={{ fontSize: '1.24rem' }}
                >
                  Total Atend.
                </th>
              </tr>
            </thead>
            <tbody
              className="text-dark  table-bordered border-2"
              style={{
                fontSize: '1.25rem',
                borderColor: '#D9D9D9',
                height: '100px',
              }}
            >
              {atendimentoData.qtdeAtendimentoUsuario?.map((atendimento) => (
                <tr
                  key={atendimento.nomeUsuario}
                  className="text-dark border-2"
                  style={{
                    fontSize: '1.25rem',
                  }}
                >
                  <td className="col-1 text-center border-2 p-1">
                    {getInitials(atendimento.nomeUsuario)}
                  </td>
                  <td className="col-4 p-1">{atendimento.nomeUsuario}</td>
                  <td className="col-1 text-center border-2 p-1">
                    {atendimento.qtdeFeedbackPositivo}
                  </td>
                  <td className="col-1 text-center border-2 p-1">
                    {atendimento.qtdeFeedbackMedio}
                  </td>
                  <td className="col-1 text-center border-2 p-1">
                    {atendimento.qtdeFeedbackNegativo}
                  </td>
                  <td className="col-2 text-center border-2 p-1">
                    {atendimento.qtdeTotalAtendimento}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardUserAtendiment;
